/**
 * Writing Styles - English (US) Localization
 */

export default {
    writingStyles: {
        title: 'Writing Styles',
        description: 'Manage your patent writing styles',

        // Actions
        actions: {
            create: 'Create Style',
            edit: 'Edit',
            delete: 'Delete',
            preview: 'Preview',
            duplicate: 'Duplicate',
            export: 'Export',
            favorite: 'Add to Favorites',
            unfavorite: 'Remove from Favorites',
            selectFile: 'Select File',
            uploadFile: 'Upload File',
            download: 'Download',
            cancel: 'Cancel',
            save: 'Save',
            saving: 'Saving...',
            confirm: 'Confirm',
            viewAll: 'View All',
            collapseAll: 'Collapse All',
            expandAll: 'Expand All',
            sort: 'Sort',
            more: 'More',
        },

        // Tabs
        tabs: {
            all: 'All',
            public: 'Public',
            private: 'Private',
            favorites: 'Favorites',
        },

        // Filters
        filters: {
            region: 'Region',
            allRegions: 'All Regions',
            sourceType: 'Source Type',
            allSources: 'All Sources',
            search: 'Search styles...',
        },

        // View Modes
        viewMode: {
            grid: 'Grid View',
            list: 'List View',
        },

        // Categories
        categories: {
            all: 'All Categories',
            public: 'Public',
            private: 'Private',
            system: 'System Preset',
            custom: 'Custom',
            SYSTEM: 'System Preset',
            CUSTOM: 'Custom',
            favorites: 'Favorites',
            FAVORITES: 'Favorites',
        },

        // Source Types
        sourceTypes: {
            reference: 'From Patents & Docs',
            upload: 'File Upload',
            manual: 'Manual Input',
            pdf: 'PDF Upload',
            word: 'Word Upload',
            patent_number: 'Patent Number',
            patentNumber: 'Patent Number',
        },

        // Regions
        regions: {
            all: 'All Regions',
            USPTO: 'United States',
            us: 'United States',
            CNIPA: 'China',
            cn: 'China',
            EPO: 'Europe',
            ep: 'Europe',
            JPO: 'Japan',
            jp: 'Japan',
            KIPO: 'South Korea',
            kr: 'South Korea',
            WIPO: 'WIPO',
            wipo: 'WIPO',
            PCT: 'International (PCT)',
        },

        // Sections
        sections: {
            title: 'Title',
            titlePlaceholder: 'Enter patent title',
            abstract: 'Abstract',
            abstractPlaceholder: 'Enter patent abstract',
            claims: 'Claims',
            claimsPlaceholder: 'Enter claims',
            technicalField: 'Technical Field',
            technicalFieldPlaceholder: 'Enter technical field',
            background: 'Background',
            backgroundPlaceholder: 'Enter background technology',
            summary: 'Summary',
            summaryPlaceholder: 'Enter summary of invention',
            drawings: 'Brief Description of Drawings',
            drawingsPlaceholder: 'Enter brief description of drawings',
            detailedDescription: 'Detailed Description',
            detailedDescriptionPlaceholder: 'Enter detailed description',
        },

        // Dialog
        dialog: {
            createStyle: 'Create Writing Style',
            editStyle: 'Edit Writing Style',
            createStyleDesc: 'Learn from published patents to build your writing style',
            styleName: 'Style Name',
            styleNamePlaceholder: 'Enter a descriptive name for this style',
            description: 'Description',
            descriptionPlaceholder: 'Enter a brief description (optional)',
            region: 'Region',
            category: 'Category',
            visibility: 'Visibility',
            public: 'Team Visible',
            private: 'Private',
            publicDesc: 'Visible to all team members',
            privateDesc: 'Only visible to you',
            sourceType: 'Source Type',
            manualDesc: 'Manually input each section',
            pdfDesc: 'Upload a PDF file to extract content',
            wordDesc: 'Upload a Word document to extract content',
            patentNumberDesc: 'Fetch content from a patent number',
            uploadFile: 'Reference Documents',
            dragDropOrClick: 'Click or drag file here to upload',
            pdfWordSupport: 'Supports PDF and Word documents (max 50MB)',
            pdfOnly: 'Supports PDF files only (max 50MB)',
            wordOnly: 'Supports Word documents only (max 50MB)',
            selectFile: 'Select File',
            uploading: 'Uploading...',
            patentNumber: 'Reference Patents',
            patentNumberPlaceholder: 'e.g., US1234567A, CN201910123456A',
            fetch: 'Fetch',
            sections: 'Patent Sections',
            sectionsHint: 'Configure content for each section',
            expandAll: 'Expand all',
            collapseAll: 'Collapse all',
            sectionPlaceholder: 'Enter content for this section...',
            required: 'Required',
            chars: 'chars',
            cancel: 'Cancel',
            create: 'Create Style',
            save: 'Save Changes',
            saveChanges: 'Save Changes',
            saving: 'Saving...',

            // Visibility Dialog
            changeVisibility: 'Change Visibility',
            visibilityDescription: 'Choose the visibility settings for this writing style. Private styles are only visible to you, while public styles are visible to all team members.',
            teamVisible: 'Team Visible',
            teamVisibleDesc: 'Visible to all team members',
            privateVisible: 'Private',
            privateVisibleDesc: 'Only visible to creator',
            changeToPublicWarning: 'After changing to team visible, all members of your team will be able to view and use this style.',
            changeToPrivateWarning: 'After changing to private, only you will be able to view and use this style.',

            // Level selection (Super Admin only)
            level: 'Visibility',
            adminOnly: 'Admin Only',
            systemLevel: 'System Level',
            userLevel: 'User Level',
            systemLevelDesc: 'Visible to all users',
            userLevelDesc: 'Visible to me only',
        },

        // Create Dialog (legacy - for backward compatibility)
        createDialog: {
            title: 'Create Writing Style',
            editTitle: 'Edit Writing Style',
            basicInfo: 'Basic Information',
            name: 'Name',
            namePlaceholder: 'Enter style name',
            nameRequired: 'Name is required',
            description: 'Description',
            descriptionPlaceholder: 'Enter style description (optional)',
            region: 'Region',
            regionPlaceholder: 'Select region',
            regionRequired: 'Region is required',
            category: 'Category',
            categoryPlaceholder: 'Select category',
            tags: 'Tags',
            tagsPlaceholder: 'Add tags (press Enter)',

            sourceMethod: 'Source Method',
            selectSourceMethod: 'Select how to create this style',
            manualInput: 'Manual Input',
            manualInputDesc: 'Manually input content for each section',
            uploadPdf: 'Upload PDF',
            uploadPdfDesc: 'Upload a PDF file to extract content',
            uploadWord: 'Upload Word',
            uploadWordDesc: 'Upload a Word document to extract content',
            patentNumber: 'Patent Number',
            patentNumberDesc: 'Fetch content from a patent number',

            patentNumberLabel: 'Patent Number',
            patentNumberPlaceholder: 'Enter patent number (e.g., US1234567A)',
            patentNumberFetch: 'Fetch Patent',
            patentNumberFetching: 'Fetching...',

            fileUpload: 'File Upload',
            fileUploadDrag: 'Click or drag file to this area to upload',
            fileUploadHint: 'Support PDF and Word files, max 50MB',
            fileUploading: 'Uploading...',
            fileProcessing: 'Processing file...',
            fileProcessed: 'File processed successfully',

            sectionContent: 'Section Content',
            sectionContentDesc: 'Edit content for each section',
            sectionRequired: 'Required',
            sectionOptional: 'Optional',
            sectionPlaceholder: 'Enter content for {section}',
            sectionEmpty: 'No content',
        },

        // Preview Dialog
        preview: {
            title: 'Preview Writing Style',
            loading: 'Loading preview...',
            wordDocument: 'Word Document',
            patentNumber: 'Patent Number',
            noContent: 'No content available',
            resetZoom: 'Reset Zoom',
            sections: 'Sections',
            usageCount: 'Usage Count',
            lastUpdated: 'Last Updated',
        },
        previewDialog: {
            title: 'Preview Writing Style',
            loading: 'Loading preview...',
            sourceInfo: 'Source Information',
            sourceType: 'Source Type',
            fileName: 'File Name',
            patentNumber: 'Patent Number',
            fileSize: 'File Size',
            uploadDate: 'Upload Date',
            openFile: 'Open File',
            sections: 'Sections',
        },

        // Empty States
        empty: {
            noStyles: 'No Writing Styles',
            createFirstStyle: 'Create your first writing style to get started',
            noResults: 'No styles found',
            tryDifferentFilters: 'Try adjusting your filters or search terms',
            noStylesFound: 'No writing styles found',
        },
        emptyState: {
            title: 'No Writing Styles',
            description: 'Create your first writing style to get started',
            createButton: 'Create Style',
            noResults: 'No styles found',
            noResultsDesc: 'Try adjusting your filters or search terms',
        },

        // Stats
        stats: {
            total: 'Total Styles',
            public: 'Public Styles',
            private: 'Private Styles',
            system: 'System Styles', // ✅ Added
            custom: 'Custom Styles', // ✅ Added
            favorites: 'Favorite Styles',
        },

        // Delete Confirmation
        delete: {
            title: 'Delete Writing Style',
            message: 'Are you sure you want to delete "{name}"?',
            confirm: 'Delete',
            cancel: 'Cancel',
        },

        // Editor
        editor: {
            createTitle: 'Create Writing Style',
            editTitle: 'Edit Writing Style',
            basicInfo: 'Basic Information',
            sourceType: 'Source Type',
            referenceDoc: 'Reference Document',
            manualInput: 'Manual Input',
            patentNumber: 'Patent Number',
            patentNumberPlaceholder: 'e.g., US1234567A, CN201910123456A',
            fetch: 'Fetch',
            uploadFile: 'Upload File',
            uploadTitle: 'Click or drag patent documents here to upload',
            uploadHint: 'Upload patent documents as writing style references — PDF and Word supported (max 50MB)',
            fileProcessing: 'Processing...',
            fileCompleted: 'Completed',
            fileFailed: 'Failed',
            loadingPatentData: 'Loading patent information...',
            patentDataLoaded: 'Patent information loaded successfully',
            searchFailed: 'Search failed',
            patentDataLoadSuccess: 'Patent information loaded successfully',
            patentDataLoadFailed: 'Failed to load patent information',
            fileUploadedHint: 'File uploaded, cannot enter patent number',
            patentNumberEnteredHint: 'Patent number entered, cannot upload file',
            generateStyle: 'Generate Style',
            generateSuccess: 'Style generated successfully',
            generateFailed: 'Failed to generate style',
            manualEditMode: 'Entered manual edit mode',
            manualInputHint: 'After selecting manual input, you can edit the style content directly in the right editor',
            emptyTitle: 'Start Creating Your Writing Style',
            emptyDescription: 'Fill in basic information on the left, select reference document or manual input, then click "Generate Style" button',
            sectionsTitle: 'Edit Writing Style Content',
            sectionsHint: 'Please fill in the content for each section. Sections marked as required must be filled.',
            previewTitle: 'Style Content Preview',
            patentTitle: 'Title',
            patentAbstract: 'Abstract',
            patentClaims: 'Claims',
            patentDescription: 'Description',
            autoCreate: 'Analyze & Create',
        },

        // Messages
        messages: {
            createSuccess: 'Writing style created successfully',
            createError: 'Failed to create writing style',
            updateSuccess: 'Writing style updated successfully',
            updateError: 'Failed to update writing style',
            saveError: 'Failed to save writing style',
            deleteSuccess: 'Writing style deleted successfully',
            deleteError: 'Failed to delete writing style',
            deleteConfirm: 'Are you sure you want to delete "{name}"?',
            deleteConfirmDesc: 'This action cannot be undone',
            favoriteSuccess: 'Added to favorites',
            unfavoriteSuccess: 'Removed from favorites',
            toggleFavoriteError: 'Failed to toggle favorite',
            duplicateSuccess: 'Writing style duplicated successfully',
            duplicateError: 'Failed to duplicate writing style',
            fileUploadSuccess: 'File uploaded successfully',
            fileUploadError: 'Failed to upload file',
            patentFetchSuccess: 'Patent information fetched successfully',
            patentFetchError: 'Failed to fetch patent information',
            validationError: 'Please fill in all required fields',
            sectionValidationError: 'Please fill in all required sections',
        },

        // Card Info
        card: {
            usedTimes: 'Used {count} times',
            lastModified: 'Modified {time}',
            createdBy: 'Created by {name}',
        },

        // Patent Search Dialog
        patentSearch: {
            title: 'Search Patents',
            placeholder: 'Enter patent number, e.g., US11739015B2, CN201910123456A',
            search: 'Search',
            hint: 'Search by patent number — AI will analyze their writing patterns to build your style',
            hintBatch: 'Batch search supported: separate multiple patent numbers with comma, semicolon, space, or newline',
            searching: 'Searching...',
            noResults: 'No patents found',
            searchSuccess: 'Top {count} related patents',
            searchFailed: 'Search failed, please try again later',
            batchSearching: 'Batch searching {count} patents...',
            batchSearchSuccess: 'Batch search completed: {success} succeeded, {failed} failed, {total} patents found',
            selectedCount: '{count} patents selected',
            selectAtLeastOne: 'Please select at least one patent',
            loadDetailsFailed: 'Failed to load patent details',
            enterPatentNumber: 'Please enter a patent number',
        },
    },
};
